import TableCellRenderer from '@cp/src/components/TableCellRenderer';
import CellRendererShortNumber from '@cp/src/components/TableCellRenderer/CellRendererShortNumber';
import { ColDefsType } from '@navi/web-ui/lib/components/AgTable/types';
import { ICellEditorParams, ICellRendererParams } from 'ag-grid-community';
import { getSanitizedEnumValue, pluralisation } from 'src/utils/commonUtils';
import { Button, Tag, Typography } from '@navi/web-ui/lib/primitives';
import { ArrowDownIcon } from '@navi/web-ui/lib/icons';
import { getTagColor } from '../../CsaTicketing/utils';
import { TicketStatusMap } from '../../CsaTicketing/constants';
import styles from '../index.module.scss';
import { ITableTicket } from '../types';
import { TicketStatus } from '../../CsaTicketing/types';
import TimeAgo from '@cp/src/components/TimeAgo';
import { getFilteredOptions } from '../contants';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

export const tooltipContentClass = 'z-[var(--z-index-external-sensei-tooltip)]';

export const getTasksForMeColumnDefs = (
  viewDetailsHandler: (ticket: ITableTicket) => void,
  ticketStatusHandler: (
    value: string,
    setValue: (value: string) => void,
    stopEditing: any,
    referenceId: string
  ) => void
) => {
  return [
    {
      field: 'taskType',
      headerComponent: 'Request Type',
      width: 156,
      cellRenderer: (params: ICellRendererParams) => {
        const { requestTypeLabel, isAcknowledgedByAssignee, newUpdatesCount } = params.data || {};
        return (
          <div>
            {newUpdatesCount ? (
              <Typography variant="h6" color="#518CFF">
                {newUpdatesCount} {pluralisation('update', newUpdatesCount, 's')}
              </Typography>
            ) : null}
            <div className="flex items-center gap-2">
              {!isAcknowledgedByAssignee ? <div className={styles.newTicket} /> : null}
              <TableCellRenderer
                value={requestTypeLabel}
                toolTipContent={requestTypeLabel}
                showToolTip
                tooltipContentClass={tooltipContentClass}
              />
            </div>
          </div>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      wrapHeaderText: true,
      serverSortable: true,
      editable: true,
      singleClickEdit: true,
      cellEditor: 'basicSelectEditor',
      cellEditorPopup: true,
      cellEditorParams: (params: ICellEditorParams) => {
        const { status, referenceId, loanAccountNumber } = params?.data || {};
        const isTicketDone = status === TicketStatus.CLOSED || status === TicketStatus.FORCE_CLOSED;
        const options = getFilteredOptions(status);
        return {
          values: options,
          valueListGap: 0,
          selectedValues: status,
          multiSelect: false,
          selectPickerWrapperClass: styles.selectPicker,
          disabled: isTicketDone,
          deleteSelectedValueFromMap: false,
          callBackFn: (value: string, setValue: (val: string) => void) => {
            if (status === value) return;
            addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_TASK_STATUS_DROPDOWN_CLICKED, {
              lan: loanAccountNumber,
              statusChangesTo: value,
              ticketId: referenceId
            });
            ticketStatusHandler(value, setValue, params?.stopEditing, referenceId);
          }
        };
      },
      cellRenderer: (params: ICellRendererParams) => {
        const { status } = params?.data || {};
        const isTicketDone = status === TicketStatus.CLOSED || status === TicketStatus.FORCE_CLOSED;
        if (status) {
          return (
            <div
              style={{
                cursor: isTicketDone ? 'not-allowed' : 'pointer'
              }}
              className={styles.ticketStatusWrapper}
            >
              <Tag
                label={getSanitizedEnumValue(TicketStatusMap, status)}
                variant="transparent"
                color={getTagColor(status)}
              />
              <ArrowDownIcon size="sm" color={isTicketDone ? '#D9D9D9' : 'var(--grayscale-3)'} />
            </div>
          );
        }
        return '-';
      }
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      width: 220,
      wrapHeaderText: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { customerName } = params.data || {};
        return (
          <TableCellRenderer
            value={customerName}
            toolTipContent={customerName}
            showToolTip
            tooltipContentClass={tooltipContentClass}
          />
        );
      }
    },
    {
      field: 'loanAccountNumber',
      headerName: 'LAN',
      width: 140,
      wrapHeaderText: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { loanAccountNumber } = params.data || {};
        return (
          <TableCellRenderer
            value={loanAccountNumber}
            toolTipContent={loanAccountNumber}
            showToolTip
            tooltipContentClass={tooltipContentClass}
          />
        );
      }
    },
    {
      field: 'overdueAmount',
      headerName: 'Overdue',
      width: 116,
      wrapHeaderText: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { overdueAmount } = params?.data || {};
        return (
          <CellRendererShortNumber
            value={overdueAmount?.value}
            isCurrency
            significantDigits={2}
            tooltipContentClass={tooltipContentClass}
          />
        );
      }
    },
    {
      field: 'requestedBy',
      headerName: 'Requested By',
      width: 200,
      wrapHeaderText: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { requestedBy } = params.data || {};
        return (
          <TableCellRenderer
            value={requestedBy?.name}
            toolTipContent={requestedBy?.name}
            showToolTip
            tooltipContentClass={tooltipContentClass}
          />
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Raised at',
      width: 110,
      wrapHeaderText: true,
      serverSortable: true,
      cellRenderer: (params: ICellRendererParams) => {
        const { createdAt, referenceId } = params.data || {};
        return <TimeAgo key={referenceId} epoch={createdAt} ellipsisClass={styles.ellipsisClass} />;
      }
    },
    {
      field: 'referenceId',
      headerName: 'Actions',
      width: 120,
      wrapHeaderText: true,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Button variant="text" onClick={() => viewDetailsHandler(params?.data)}>
            View Details
          </Button>
        );
      }
    }
  ] as ColDefsType[];
};
